import getStore from '@/store';
import { IEditorType, ImageEditorPath } from '@/types';
import { getQueryParam } from './query';

/**
 * 设置 OBS 图片缩略图宽度
 */
export const setThumbnailWidth = (): number => {
    return window.devicePixelRatio > 1 ? 640 : 320;
};

/**
 * 判断是否是 Mac 系统电脑
 */
export const isMac = (ua: string) => {
    return /(macintosh)|(mac os x)/.test(ua);
};

/**
 * 判断是否是 Windows 系统电脑
 */
export const isWin32 = (ua: string) => {
    return /(window)/.test(ua) && /(win32)|(wow32)/.test(ua);
};

/**
 * 判断是否是 Windows 系统电脑
 */
export const isWin64 = (ua: string) => {
    return /(window)/.test(ua) && /(win64)|(wow64)/.test(ua);
};

/**
 * 判断是否是 Linux 系统电脑
 */
export const isLinux = (ua: string) => {
    return /linux/.test(ua);
};

/**
 * 判断是否来自 Mac 端 Safari 内核
 */
export const isFromMacSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('usesafari') > -1) {
        return true;
    }

    return false;
};

export const isFromSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

export const getSysType = () => {
    const ua = window.navigator.userAgent.toLowerCase();

    if (isMac(ua)) {
        return 'mac';
    }

    if (isWin32(ua)) {
        return 'win32';
    }

    if (isWin64(ua)) {
        return 'win64';
    }

    if (isLinux(ua)) {
        return 'linux';
    }

    return '';
};

export const copyTextToClipboard = (str: string) => {
    navigator.clipboard.writeText(str);
};

// 判断是否来自华为PC预装定制版内嵌
export const isFromHuaweiCustom = () => {
    const ua = navigator.userAgent.toLowerCase();

    return ua.includes('channel:zhyshop');
};

// 是否处于容器内
export const isInContainer = (el: Element, container: HTMLElement) => {
    if (!el || !container) return false;

    const elRect = el.getBoundingClientRect();
    let containerRect;

    if ([window, document, document.documentElement, null, undefined].includes(container)) {
        containerRect = {
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
            left: 0,
        };
    } else {
        containerRect = container.getBoundingClientRect();
    }

    return (
        elRect.top < containerRect.bottom &&
        elRect.bottom > containerRect.top &&
        elRect.right > containerRect.left &&
        elRect.left < containerRect.right
    );
};

/**
 * 自动处理带有 exif orientation 的图片
 */
export const imageAutoOrient = (imageUrl: string) => {
    const [host, querystring] = imageUrl.split('?');

    if (!querystring) return `${imageUrl}?imageView2/2/auto-orient`;
    if (querystring.indexOf('imageView2/2') === -1) return `${imageUrl}&imageView2/2/auto-orient`;
    if (/imageView2[^&]+\/auto-orient/.test(querystring)) return imageUrl;

    return `${host}?${querystring.replace(/(imageView2[^&]+)/, (matched) => `${matched}/auto-orient`)}`;
};

/**
 * 移除首尾多余括号 ()（）
 */

export const removeBrackets = (text: string) => {
    return text.replace(/\s*[\(\)\（\）]/g, '');
};

/**
 * 检查 url path 是否为图片编辑器
 *
 * @param pathName - 路径
 */
export const checkIsImageEditorPath = (pathName?: string) => {
    pathName = pathName ?? window.location.pathname;

    const pathReg = new RegExp(`${ImageEditorPath}(?!\w)`);
    return pathReg.test(pathName);
};

/**
 * 是否为图片编辑器
 */
export const checkImageEditor = () => {
    const editorType = getStore().getState().editorModel.editorType;
    return (
        editorType === IEditorType.imageEditor ||
        getQueryParam('editorType') === IEditorType.imageEditor ||
        checkIsImageEditorPath()
    );
};

export function parseJSON<T>(jsonStr: string) {
    try {
        return JSON.parse(jsonStr) as T;
    } catch (e) {
        console.log(e);
        return null;
    }
}

export const sleep = (timeout = 1000) =>
    new Promise((resolve) => {
        setTimeout(resolve, timeout);
    });

const workerScript = `
    onmessage = function(event) {
        const { timeout } = event.data;

        setTimeout(() => {
            postMessage('done');
        }, timeout);
    };
`;
export const workerSleep = (timeout = 1000) => {
    return new Promise<void>((resolve) => {
        const blob = new Blob([workerScript], {
            type: 'application/javascript',
        });
        const worker = new Worker(URL.createObjectURL(blob));
        worker.onmessage = function () {
            resolve();
            worker.terminate(); // 结束工作者线程
        };
        worker.postMessage({ timeout });
    });
};

/**
 * 判断是否为 JSON 字符串
 *
 * @param str - 字符串
 */
export const isJsonStr = (str: string) => {
    if (typeof str === 'string') {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }
    return false;
};

/**
 * 是否是 Safari 浏览器
 */
export const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('safari') > -1 && ua.indexOf('chrome') === -1;
};

/**
 * 判断是否是移动端
 */
export const isMobile = () => {
    const ua = navigator.userAgent.toLowerCase();
    return /mobile/i.test(ua);
};

/**
 * 根据当前颜色值获取对应深色值
 *
 * @param color - 需要设置的颜色值
 * @param amount - 数值越大则越深
 * @example
 * ``` typescript
 * subtractLight('#fff', 6)
 * ```
 */
export const subtractLight = (color: string, amount: number): string => {
    const cc = parseInt(color, 16) - amount;
    const c = cc < 0 ? 0 : cc;
    return c.toString(16).length > 1 ? c.toString(16) : `0${c.toString(16)}`;
};

export const darken = (color: string, amount: number): string | undefined => {
    if (color.length < 6) {
        throw new Error('请遵循 #ffffff 格式');
    }
    color = color.indexOf('#') >= 0 ? color.substring(1, color.length) : color;
    amount = Math.trunc((255 * amount) / 100);
    return `#${subtractLight(color.substring(0, 2), amount)}${subtractLight(
        color.substring(2, 4),
        amount,
    )}${subtractLight(color.substring(4, 6), amount)}`;
};

/**
 * 判断是否十六进制颜色值
 *
 * @param color - 十六进制颜色值
 * @example
 * ``` typescript
 * isHexColor('#fff')
 * ```
 */
export const isHexColor = (color: string): boolean => {
    const reg = /^#([0-9a-fA-F]{3}|[0-9a-fA-f]{6})$/;
    return reg.test(color);
};

/**
 * 十六进制颜色值转 RGB 颜色值
 *
 * @param hex - 十六进制颜色值
 * @param opacity - 透明度 0 - 1之间的值
 * @example
 * ``` typescript
 * hexToRGB('#fff', 0.5)
 * ```
 */
export const hexToRGB = (hex: string, opacity?: number): string => {
    let sHex = hex.toLowerCase();
    if (isHexColor(hex)) {
        if (sHex.length === 4) {
            let sColorNew = '#';
            for (let i = 1; i < 4; i += 1) {
                sColorNew += sHex.slice(i, i + 1).concat(sHex.slice(i, i + 1));
            }
            sHex = sColorNew;
        }
        const sColorChange: number[] = [];
        for (let i = 1; i < 7; i += 2) {
            sColorChange.push(parseInt('0x' + sHex.slice(i, i + 2)));
        }
        return opacity ? 'RGBA(' + sColorChange.join(',') + ',' + opacity + ')' : 'RGB(' + sColorChange.join(',') + ')';
    }
    return sHex;
};

export const getScale = (wrapWidth: number, wrapHeight: number, width: number, height: number) => {
    // 图片宽高比和屏幕��高比
    const imageAspect = width / height;
    const screenAspect = wrapWidth / wrapHeight;

    // 计算缩放比
    const scale = imageAspect > screenAspect ? wrapWidth / width : wrapHeight / height;
    if (scale > 1) {
        return 1;
    }
    return scale;
};
